/**
 * バックグラウンド同期エンジンフック
 *
 * オンライン復帰時にIndexedDBの未同期データを自動的にサーバーに送信する。
 * - 未同期プログレス
 * - 未同期ハイライト（作成・更新・削除）
 * あわせて、ダウンロード済みエピソードの進捗をサーバから取り込み、オフラインでも続きから開けるようにする。
 */
import axios from 'axios'
import type React from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { getProductEpisodesProgress, updateEpisodeProgress } from '@/api/api'
import { createHighlightOnServer, deleteHighlightOnServer, updateHighlightOnServer } from '@/api/services/highlight'
import { cacheEpisodeProgress } from '@/api/services/progress'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import {
  deleteOfflineHighlight,
  getAllOfflineEpisodes,
  getUnsyncedHighlights,
  getUnsyncedProgress,
  markHighlightSynced,
  type OfflineHighlight,
} from '@/lib/db'
import { MUSIC_DOMAIN } from '@/pages/music/constants'
import { useProgressStore } from '@/store/useProgressStore'

/** ダウンロード済みエピソードの進捗を取り込む最小間隔（タブ復帰のたびに叩かないため） */
const PULL_INTERVAL_MS = 5 * 60 * 1000
let lastPulledAt = 0

/**
 * 未同期プログレスをサーバーに送信
 */
const syncProgress = async (): Promise<number> => {
  const unsynced = await getUnsyncedProgress()
  let count = 0

  for (const p of unsynced) {
    try {
      await updateEpisodeProgress(p.episodeId, {
        progress: p.progress,
        cfi: p.cfi ?? undefined,
        locator: p.locator ?? undefined,
        is_completed: p.isCompleted,
      })
      useProgressStore.getState().applyEpisodeProgress(p.episodeId, p.progress, p.isCompleted)
      count++
    } catch (error) {
      console.warn('プログレス同期失敗:', { episodeId: p.episodeId, error })
    }
  }

  return count
}

/**
 * ダウンロード済みエピソードの進捗をサーバから取り込む（他端末で読み進めた分を機内でも使えるように）
 *
 * 作品ごとに一括取得する。音楽は作品（アルバム）数が多く、続きからの再生も使わないため対象外。
 */
const pullDownloadedProgress = async (): Promise<number> => {
  if (Date.now() - lastPulledAt < PULL_INTERVAL_MS) return 0
  lastPulledAt = Date.now()

  const episodes = await getAllOfflineEpisodes()
  const idsByProduct = new Map<number, Set<number>>()
  for (const ep of episodes) {
    if (ep.domain === MUSIC_DOMAIN) continue
    const ids = idsByProduct.get(ep.productId) ?? new Set<number>()
    ids.add(ep.episodeId)
    idsByProduct.set(ep.productId, ids)
  }

  let count = 0
  for (const [productId, ids] of idsByProduct) {
    try {
      const list = await getProductEpisodesProgress(productId)
      for (const p of list) {
        if (!ids.has(p.episode_id)) continue
        await cacheEpisodeProgress(p)
        count++
      }
    } catch (error) {
      console.warn('ダウンロード済みエピソードの進捗取得失敗:', { productId, error })
    }
  }
  return count
}

/**
 * 未同期ハイライトをサーバーに送信
 */
const syncHighlights = async (): Promise<number> => {
  const unsynced = await getUnsyncedHighlights()
  let count = 0

  for (const h of unsynced) {
    try {
      if (h.deleted) {
        await syncDeletedHighlight(h)
      } else if (h.serverId) {
        await syncUpdatedHighlight(h)
      } else {
        await syncNewHighlight(h)
      }
      count++
    } catch (error) {
      console.warn('ハイライト同期失敗:', { localId: h.localId, error })
    }
  }

  return count
}

const isNotFound = (error: unknown): boolean => axios.isAxiosError(error) && error.response?.status === 404

const syncNewHighlight = async (h: OfflineHighlight) => {
  const created = await createHighlightOnServer({ ...h.data, color: h.color, note: h.note })
  await markHighlightSynced(h.localId, h.updatedAt, created.id)
}

const syncUpdatedHighlight = async (h: OfflineHighlight) => {
  if (!h.serverId) return
  try {
    await updateHighlightOnServer(h.serverId, {
      color: h.color,
      note: h.note ?? undefined,
    })
  } catch (error) {
    // 他端末で削除済み
    if (!isNotFound(error)) throw error
    await deleteOfflineHighlight(h.localId)
    return
  }
  await markHighlightSynced(h.localId, h.updatedAt)
}

const syncDeletedHighlight = async (h: OfflineHighlight) => {
  if (h.serverId) {
    try {
      await deleteHighlightOnServer(h.serverId)
    } catch (error) {
      if (!isNotFound(error)) throw error
    }
  }
  await deleteOfflineHighlight(h.localId)
}

/**
 * 全未同期データを同期
 */
const runSync = async (isSyncingRef: React.RefObject<boolean>): Promise<{ progress: number; highlights: number }> => {
  if (isSyncingRef.current) return { progress: 0, highlights: 0 }
  isSyncingRef.current = true

  try {
    const [progress, highlights] = await Promise.all([syncProgress(), syncHighlights()])
    await pullDownloadedProgress()
    return { progress, highlights }
  } finally {
    isSyncingRef.current = false
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * アプリのルートレベルでマウントし、オンライン復帰時に自動同期を実行する
 */
export const useSyncEngine = () => {
  const { isOnline } = useOnlineStatus()
  const prevOnlineRef = useRef(isOnline)
  const isSyncingRef = useRef(false)

  const sync = useCallback(async () => {
    try {
      const result = await runSync(isSyncingRef)
      const total = result.progress + result.highlights
      if (total > 0) {
        console.info(
          `[SyncEngine] ${total}件のデータを同期しました (progress=${result.progress}, highlights=${result.highlights})`,
        )
      }
    } catch (error) {
      console.warn('[SyncEngine] 同期に失敗:', error)
    }
  }, [])

  // オンライン復帰時に自動同期
  useEffect(() => {
    const wasOffline = !prevOnlineRef.current
    prevOnlineRef.current = isOnline

    if (isOnline && wasOffline) {
      console.info('[SyncEngine] オンライン復帰 → 同期開始')
      sync()
    }
  }, [isOnline, sync])

  // 初回マウント時 + タブ復帰時（iOS Safari スリープ対策）に同期
  useEffect(() => {
    // 初回マウント時（既にオンラインならすぐ同期）
    if (navigator.onLine) {
      sync()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        sync()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [sync])

  return { sync, isOnline }
}
